import React, { useContext, useEffect, useMemo, useState } from "react";
import { Image, Linking, Modal, Platform, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { BasePage } from "../../genericPages/helpers/BasePage";
import { FindifyFooterLogo, BottomNavigationPadding } from "../../genericPages/components/FindifyNavigation";
import { FindifyAppHeader } from "../../genericPages/components/FindifyAppHeader";
import { PlaceText } from "../../genericPages/components/PlaceText";
import { PlaceColors } from "../../genericPages/helpers/PlaceColors";
import { getApiFromModule } from "../../genericPages/helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "../../genericPages/helpers/DetailsHelpers";
import { getTranslatedString } from "../../genericPages/helpers/FindifyTranslatedString";
import { useSeoTitle } from "../../genericPages/helpers/SeoTitle";
import { FindifyHTMLRender } from "../../genericPages/helpers/FindifyHTMLRender";

const isEnglishLanguage = (language?: string) => language?.startsWith("en");
const localizedText = (findifyDataContext: FindifyDataContextType | null, pt: string, en: string) => {
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const stripHtml = (html: string) => {
    return (html ?? "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/\s+\n/g, "\n")
        .replace(/\n\s+/g, "\n")
        .trim();
}

const formatPrice = (item: any, language?: string) => {
    const value = Number(String(item?.price_price ?? "").replace(",", "."));
    const currency = item?.price_currency || "EUR";
    if (!Number.isFinite(value)) return "";

    try {
        return new Intl.NumberFormat(language || "pt-PT", {
            style: "currency",
            currency,
        }).format(value);
    } catch {
        return `${value.toFixed(2)} ${currency}`;
    }
}

const openExternalUrl = (url?: string) => {
    if (!url) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
    }
    Linking.openURL(url);
}

const getProductUrl = (item: any) => item?.price_url || item?.item_url || item?.iframe_url || item?.page_url;

const getProductCategories = (item: any) => (item?.categories ?? [])
    .map((line: any) => line?.category)
    .filter(Boolean);

export const StoreTab = ({ navigation }: any) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    useSeoTitle(localizedText(findifyDataContext, "Loja", "Shop"), "Museu da Terra de Miranda");

    useEffect(() => {
        let mounted = true;
        const api = getApiFromModule("store", findifyDataContext as FindifyDataContextType);
        setLoading(true);
        api?.getList(0, 100, null, {}, null)
            .then((res: any) => {
                if (!mounted) return;
                const loadedItems = [...(res?.results ?? [])].sort((a: any, b: any) => {
                    return String(getTranslatedString(a?.title ?? a?.name, findifyDataContext)).localeCompare(
                        String(getTranslatedString(b?.title ?? b?.name, findifyDataContext))
                    );
                });
                setItems(loadedItems);
            })
            .catch(() => {
                if (!mounted) return;
                setItems([]);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        }
    }, [findifyDataContext?.workspace?.workspace_id, findifyDataContext?.language]);

    const categories = useMemo(() => {
        const byId = new Map<number, any>();
        items.forEach((item) => {
            getProductCategories(item).forEach((category: any) => {
                if (category?.category_id) byId.set(category.category_id, category);
            });
        });
        return Array.from(byId.values()).sort((a: any, b: any) => {
            return String(getTranslatedString(a?.name, findifyDataContext)).localeCompare(String(getTranslatedString(b?.name, findifyDataContext)));
        });
    }, [items, findifyDataContext?.language]);

    const filteredItems = selectedCategoryId
        ? items.filter((item) => getProductCategories(item).some((category: any) => category.category_id === selectedCategoryId))
        : items;

    return <BasePage navigation={navigation}>
        <View style={[styles.page, { backgroundColor: colors.background.get() }]}>
            <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <ScrollView
                bounces={false}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={[
                    styles.content,
                    isTablet ? styles.contentTablet : null,
                    isLandscapeTablet ? styles.contentLandscapeTablet : null,
                ]}>
                <View style={styles.titleBlock}>
                    <PlaceText style={[styles.title, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Loja", "Shop")}</PlaceText>
                    <PlaceText style={[styles.subtitle, { color: colors.text.withAlpha(210) }]}>
                        {localizedText(findifyDataContext, "Artigos disponíveis para compra online.", "Items available for online purchase.", "Articles disponibles à l'achat en ligne.")}
                    </PlaceText>
                </View>

                {categories.length > 0 ? (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoriesRow}>
                        <Pressable
                            style={[
                                styles.categoryChip,
                                { borderColor: colors.primary.withAlpha(30) },
                                selectedCategoryId === null ? { backgroundColor: colors.primary.get() } : null,
                            ]}
                            onPress={() => setSelectedCategoryId(null)}>
                            <PlaceText style={[
                                styles.categoryChipText,
                                { color: selectedCategoryId === null ? "#fff" : colors.primary.get() },
                            ]}>{localizedText(findifyDataContext, "Todos", "All", "Tous")}</PlaceText>
                        </Pressable>
                        {categories.map((category: any) => {
                            const selected = selectedCategoryId === category.category_id;
                            return <Pressable
                                key={`store-category-${category.category_id}`}
                                style={[
                                    styles.categoryChip,
                                    { borderColor: colors.primary.withAlpha(30) },
                                    selected ? { backgroundColor: colors.primary.get() } : null,
                                ]}
                                onPress={() => setSelectedCategoryId(category.category_id)}>
                                <PlaceText style={[
                                    styles.categoryChipText,
                                    { color: selected ? "#fff" : colors.primary.get() },
                                ]}>{getTranslatedString(category?.name, findifyDataContext)}</PlaceText>
                            </Pressable>
                        })}
                    </ScrollView>
                ) : null}

                {loading ? (
                    <View style={styles.emptyBlock}>
                        <PlaceText style={[styles.emptyText, { color: colors.text.withAlpha(170) }]}>{localizedText(findifyDataContext, "A carregar artigos...", "Loading items...", "Chargement des articles...")}</PlaceText>
                    </View>
                ) : filteredItems.length === 0 ? (
                    <View style={styles.emptyBlock}>
                        <PlaceText style={[styles.emptyText, { color: colors.text.withAlpha(170) }]}>{localizedText(findifyDataContext, "Não existem artigos disponíveis.", "No items available.", "Aucun article disponible.")}</PlaceText>
                    </View>
                ) : (
                    <View style={[styles.productsGrid, isLandscapeTablet ? styles.productsGridLandscapeTablet : null]}>
                        {filteredItems.map((item: any) => {
                            const image = getFeaturedImageUrl(item);
                            const title = getTranslatedString(item?.title ?? item?.name, findifyDataContext);
                            const summary = stripHtml(getTranslatedString(item?.resume, findifyDataContext) || "");
                            const price = formatPrice(item, findifyDataContext?.language);
                            const productUrl = getProductUrl(item);

                            return <Pressable
                                key={`store-item-${item.item_id}`}
                                style={[
                                    styles.productCard,
                                    isLandscapeTablet ? styles.productCardLandscapeTablet : null,
                                    { borderColor: colors.primary.withAlpha(24), backgroundColor: colors.background.solidAlphaWhiteBase(54) },
                                ]}
                                onPress={() => setSelectedItem(item)}>
                                {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.productImage} /> : (
                                    <View style={[styles.productImage, styles.productImageFallback, { backgroundColor: colors.primary.withAlpha(10) }]}>
                                        <Icon name="bag-outline" size={34} color={colors.primary.withAlpha(120)} />
                                    </View>
                                )}
                                <View style={styles.productBody}>
                                    <View style={styles.productHeader}>
                                        <PlaceText numberOfLines={2} style={[styles.productTitle, { color: colors.primary.get() }]}>{title}</PlaceText>
                                        {!!price && <PlaceText style={[styles.productPrice, { color: colors.text.get() }]}>{price}</PlaceText>}
                                    </View>
                                    {!!summary && <PlaceText numberOfLines={2} style={[styles.productSummary, { color: colors.text.withAlpha(190) }]}>{summary}</PlaceText>}
                                    <View style={styles.productActions}>
                                        <Pressable
                                            style={[styles.detailsButton, { borderColor: colors.primary.withAlpha(45) }]}
                                            onPress={() => setSelectedItem(item)}>
                                            <PlaceText style={[styles.detailsButtonText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Detalhes", "Details", "Détails")}</PlaceText>
                                        </Pressable>
                                        <Pressable
                                            disabled={!productUrl}
                                            style={[
                                                styles.buyButton,
                                                { backgroundColor: productUrl ? colors.primary.get() : colors.primary.withAlpha(80) },
                                            ]}
                                            onPress={() => openExternalUrl(productUrl)}>
                                            <PlaceText style={styles.buyButtonText}>{localizedText(findifyDataContext, "Comprar", "Buy", "Acheter")}</PlaceText>
                                        </Pressable>
                                    </View>
                                </View>
                            </Pressable>
                        })}
                    </View>
                )}
                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </ScrollView>
            <ProductDetailsModal
                item={selectedItem}
                findifyDataContext={findifyDataContext}
                onClose={() => setSelectedItem(null)}
            />
        </View>
    </BasePage>;
};

const ProductDetailsModal = ({
    item,
    findifyDataContext,
    onClose,
}: {
    item: any,
    findifyDataContext: FindifyDataContextType | null,
    onClose: () => void,
}) => {
    const colors = PlaceColors(findifyDataContext);
    if (!item) return null;

    const image = getFeaturedImageUrl(item);
    const title = getTranslatedString(item?.title ?? item?.name, findifyDataContext);
    const summary = stripHtml(getTranslatedString(item?.resume, findifyDataContext) || "");
    const description = getTranslatedString(item?.description, findifyDataContext);
    const price = formatPrice(item, findifyDataContext?.language);
    const productUrl = getProductUrl(item);

    return <Modal visible={!!item} transparent={true} animationType="slide" onRequestClose={onClose}>
        <View style={styles.detailsModalBackdrop}>
            <View style={[styles.detailsModalCard, { backgroundColor: colors.background.get() }]}>
                <View style={styles.detailsModalHeader}>
                    <PlaceText style={[styles.detailsModalEyebrow, { color: colors.secondary.get() }]}>{localizedText(findifyDataContext, "Loja", "Shop")}</PlaceText>
                    <Pressable style={[styles.detailsModalClose, { backgroundColor: colors.primary.withAlpha(12) }]} onPress={onClose}>
                        <Icon name="close-outline" size={26} color={colors.primary.get()} />
                    </Pressable>
                </View>
                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.detailsModalScroll}>
                    {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.detailsModalImage} /> : null}
                    <PlaceText style={[styles.detailsModalTitle, { color: colors.primary.get() }]}>{title}</PlaceText>
                    {!!price && <PlaceText style={[styles.detailsModalPrice, { color: colors.text.get() }]}>{price}</PlaceText>}
                    {!!summary && <PlaceText style={[styles.detailsModalSummary, { color: colors.text.withAlpha(190) }]}>{summary}</PlaceText>}
                    {!!description ? (
                        <View style={styles.detailsDescription}>
                            <FindifyHTMLRender
                                htmlCode={description}
                                baseStyles={{
                                    color: colors.text.get(),
                                    fontSize: 16,
                                    lineHeight: 24,
                                }}
                            />
                        </View>
                    ) : null}
                </ScrollView>
                <Pressable
                    disabled={!productUrl}
                    style={[styles.detailsModalBuyButton, { backgroundColor: productUrl ? colors.primary.get() : colors.primary.withAlpha(80) }]}
                    onPress={() => openExternalUrl(productUrl)}>
                    <PlaceText style={styles.detailsModalBuyText}>{localizedText(findifyDataContext, "Comprar", "Buy", "Acheter")}</PlaceText>
                    <Icon name="open-outline" size={19} color="#fff" />
                </Pressable>
            </View>
        </View>
    </Modal>
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
    },
    content: {
        width: "100%" as any,
        alignSelf: "center",
        paddingHorizontal: 22,
        paddingTop: 26,
        paddingBottom: BottomNavigationPadding + 36,
        maxWidth: 760,
    },
    contentTablet: {
        maxWidth: 860,
        paddingHorizontal: 40,
    },
    contentLandscapeTablet: {
        maxWidth: 1180,
    },
    titleBlock: {
        marginBottom: 20,
    },
    title: {
        fontFamily: "Georgia",
        fontSize: 43,
        lineHeight: 48,
        fontWeight: "700",
    },
    subtitle: {
        marginTop: 6,
        fontSize: 18,
        lineHeight: 27,
    },
    categoriesRow: {
        gap: 10,
        paddingBottom: 18,
    },
    categoryChip: {
        borderWidth: 1,
        borderRadius: 999,
        paddingHorizontal: 15,
        paddingVertical: 9,
    },
    categoryChipText: {
        fontSize: 14,
        lineHeight: 18,
        fontWeight: "700",
    },
    productsGrid: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 12,
    },
    productsGridLandscapeTablet: {
        alignItems: "stretch",
    },
    productCard: {
        width: "48%" as any,
        borderWidth: 1,
        borderRadius: 7,
        overflow: "hidden",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.08,
        shadowRadius: 10,
        elevation: 2,
    },
    productCardLandscapeTablet: {
        width: "31.9%" as any,
    },
    productImage: {
        width: "100%",
        aspectRatio: 1.05,
    },
    productImageFallback: {
        alignItems: "center",
        justifyContent: "center",
    },
    productBody: {
        flex: 1,
        minWidth: 0,
        padding: 10,
        gap: 8,
    },
    productHeader: {
        gap: 3,
    },
    productTitle: {
        fontFamily: "Georgia",
        fontSize: 17,
        lineHeight: 21,
        fontWeight: "700",
    },
    productPrice: {
        fontSize: 15,
        lineHeight: 19,
        fontWeight: "800",
    },
    productSummary: {
        fontSize: 12,
        lineHeight: 16,
    },
    productActions: {
        flexDirection: "column",
        alignItems: "center",
        gap: 7,
        marginTop: 2,
    },
    detailsButton: {
        width: "100%",
        minHeight: 38,
        borderRadius: 4,
        borderWidth: 1,
        paddingHorizontal: 10,
        alignItems: "center",
        justifyContent: "center",
    },
    detailsButtonText: {
        fontSize: 13,
        lineHeight: 17,
        fontWeight: "800",
    },
    buyButton: {
        width: "100%",
        minHeight: 38,
        borderRadius: 4,
        paddingHorizontal: 10,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    buyButtonText: {
        color: "#fff",
        fontSize: 13,
        lineHeight: 17,
        fontWeight: "800",
        textTransform: "uppercase",
        letterSpacing: 1,
    },
    emptyBlock: {
        minHeight: 160,
        alignItems: "center",
        justifyContent: "center",
    },
    emptyText: {
        fontSize: 16,
        lineHeight: 24,
        textAlign: "center",
    },
    detailsModalBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.48)",
        justifyContent: "flex-end",
    },
    detailsModalCard: {
        maxHeight: "88%" as any,
        borderTopLeftRadius: 18,
        borderTopRightRadius: 18,
        overflow: "hidden",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.18,
        shadowRadius: 18,
        elevation: 8,
    },
    detailsModalHeader: {
        minHeight: 56,
        paddingHorizontal: 20,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    detailsModalEyebrow: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: "800",
        letterSpacing: 2,
        textTransform: "uppercase",
    },
    detailsModalClose: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: "center",
        justifyContent: "center",
    },
    detailsModalScroll: {
        paddingHorizontal: 20,
        paddingBottom: 18,
    },
    detailsModalImage: {
        width: "100%",
        aspectRatio: 1.45,
        borderRadius: 8,
        marginBottom: 18,
    },
    detailsModalTitle: {
        fontFamily: "Georgia",
        fontSize: 29,
        lineHeight: 34,
        fontWeight: "700",
    },
    detailsModalPrice: {
        marginTop: 8,
        fontSize: 21,
        lineHeight: 26,
        fontWeight: "800",
    },
    detailsModalSummary: {
        marginTop: 10,
        fontSize: 15,
        lineHeight: 22,
    },
    detailsDescription: {
        marginTop: 14,
    },
    detailsModalBuyButton: {
        minHeight: 54,
        marginHorizontal: 20,
        marginBottom: 18,
        borderRadius: 5,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
    },
    detailsModalBuyText: {
        color: "#fff",
        fontSize: 16,
        lineHeight: 21,
        fontWeight: "800",
        textTransform: "uppercase",
        letterSpacing: 1,
    },
});
